import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "components/Common/Breadcrumb";
import ImageUpload from "components/Common/ImageUpload";
import { Container, Card, CardBody, Button, Row, Col, Form, Label, Input, FormFeedback, Spinner, Alert } from "reactstrap";
import ShimmerLoading from "components/Common/Shimmer";
import { simpleObjectToFormData } from "utils/formData";
import { useFormik } from "formik";
import { useParams, useNavigate } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { DataSchema } from "model/contents/content.schema";
import { useToasts } from "utils/useHotToast";
import ToastNotification from "components/Toast/ToastNotification";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import { useGetContentByIdQuery, useAddContentMutation, useUpdateContentMutation } from "services/contents/api";
import DatePicker from "react-tailwindcss-datepicker";
import { getImageUrl, ImageSourceType } from "utils/imageUtils";

const statusTypes = [
  { value: "highlight", label: "ไฮไลท์ทันที่" },
  { value: "normal", label: "ข่าวทั่วไป" },
];

const ContentForm = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const title = id ? "แก้ไขเนื้อหาของคุณ" : "สร้างเนื้อหาของคุณ";
  document.title = `${title} | ${process.env.REACT_APP_CMS_TITLE}`;

  // API
  const { data, error, isLoading } = useGetContentByIdQuery(id, {
    skip: !id,
  });
  const [addContent, { isLoading: isAdding }] = useAddContentMutation();
  const [updateContent, { isLoading: isUpdating }] = useUpdateContentMutation();

  const [contentImage, setContentImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const { addToast, removeToast } = useToasts();

  const getInitialValues = () => {
    if (id && data) {
      return data;
    }

    return {
      id: id ? parseInt(id) : undefined,
      contentName: "",
      contentUrl: "",
      contentImg: null,
      isHighlight: true,
      contentDetail: "",
      startDate: null,
      endDate: null,
    };
  };

  const handleStartDateChange = (dateRange) => {
    const newStartDate = dateRange.startDate;
    formik.setFieldValue("startDate", newStartDate);
    formik.setFieldTouched("startDate", true);

    // Clear any previous errors
    formik.setFieldError("startDate", undefined);
    formik.setFieldError("endDate", undefined);

    // If end date is before the new start date, update it to be the same as start date
    if (formik.values.endDate && new Date(newStartDate) > new Date(formik.values.endDate)) {
      formik.setFieldValue("endDate", newStartDate);
    }
  };

  const handleEndDateChange = (dateRange) => {
    const newEndDate = dateRange.startDate;
    formik.setFieldValue("endDate", newEndDate);
    formik.setFieldTouched("endDate", true);

    // Clear any previous errors
    formik.setFieldError("endDate", undefined);
    formik.setFieldError("startDate", undefined);

    // If start date is after the new end date, show error
    if (formik.values.startDate && new Date(newEndDate) < new Date(formik.values.startDate)) {
      formik.setFieldError("endDate", "วันที่สิ้นสุดต้องไม่น้อยกว่าวันที่เริ่มต้น");
    }
  };

  // Function to disable dates before start date
  const disableDatesBeforeStartDate = (date) => {
    if (!formik.values.startDate) return false;
    return new Date(date) < new Date(formik.values.startDate);
  };

  // Handle image selection
  const handleImageChange = (file) => {
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setContentImage(imageUrl);
      // Set the file in formik values
      formik.setFieldValue('contentImg', file);
      formik.setFieldTouched('contentImg', true, false);
      setSelectedImage(file);
      formik.setFieldValue("contentImg", file);
    }
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (contentImage && contentImage.startsWith("blob:")) {
        URL.revokeObjectURL(contentImage);
      }
    };
  }, [contentImage]);

  // Set initial image if editing
  useEffect(() => {
    if (data?.contentImg) {
      setContentImage(
        getImageUrl(data.contentImg, {
          type: ImageSourceType.LOCAL,
          path: "contents",
        })
      );
    }
  }, [data]);

  const formik = useFormik({
    initialValues: getInitialValues(),
    validateOnMount: false, // Disable validation on mount to prevent initial error state
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: DataSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const formData = selectedImage ? simpleObjectToFormData(values) : values;

        if (id) {
          // Update existing content
          const result = await updateContent({ id, formData });
          // await new Promise((resolve) => setTimeout(resolve, 1000));
          const toastId = addToast(
            <ToastNotification
              variant="success"
              icon={check}
              title="อัปเดตเนื้อหาสำเร็จ"
              description={`เนื้อหา "${values.contentName}" ถูกอัปเดตเรียบร้อยแล้ว`}
              primaryButtonText="ดูรายการเนื้อหา"
              onPrimaryClick={() => {
                removeToast(toastId);
              }}
              onSecondaryClick={() => removeToast(toastId)}
            />,
            {
              autoDismiss: true,
            }
          );
        } else {
          // Create new content
          const result = await addContent(formData);
          const toastId = addToast(
            <ToastNotification
              variant="success"
              icon={check}
              title="บันทึกเนื้อหาสำเร็จ"
              description={`เนื้อหา "${values.contentName}" ถูกบันทึกเรียบร้อยแล้ว`}
              primaryButtonText="ดูรายการเนื้อหา"
              onPrimaryClick={() => {
                removeToast(toastId);
              }}
              onSecondaryClick={() => removeToast(toastId)}
            />,
            {
              autoDismiss: true,
            }
          );
        }

        navigate("/contents");
      } catch (error) {
        const errorMessage = error?.data?.message || "ไม่สามารถดำเนินการได้ กรุณาลองใหม่อีกครั้ง";
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description={errorMessage} onSecondaryClick={() => removeToast(toastId)} />, { autoDismiss: true });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="page-content" data-testid="Content-page">
      <Container fluid>
        <Breadcrumbs title={title} breadcrumbItem={title} back="กลับหน้าจัดการเนื้อหาบนเว็บ" />
        <div className="mb-3">
          <Form onSubmit={formik.handleSubmit}>
            <Card className="card-info mt-4">
              {isLoading ? (
                <div className="text-center">
                  <ShimmerLoading fullWidth={true} height={800} />
                </div>
              ) : error ? (
                <Alert color="danger">{error.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}</Alert>
              ) : (
                <CardBody>
                  <h2 className="fw-semibold">{id ? "แก้ไขข้อมูลเนื้อหาของคุณ" : "ใส่ข้อมูลเนื้อหาของคุณ"}</h2>
                  <hr className="divider-line" />

                  <div className="d-flex flex-column mt-3 gap-4">
                    {/* TITLE */}
                    <Row>
                      <Col md={2} className="d-flex align-items-start">
                        <Label className="text-xl fw-semibold my-auto">ชื่อหัวข้อ</Label>
                      </Col>
                      <Col md={10}>
                        <Input type="text" name="contentName" placeholder="กรอกชื่อหัวข้อ" value={formik.values.contentName} onChange={formik.handleChange} onBlur={formik.handleBlur} invalid={formik.touched.contentName && formik.errors.contentName} />
                        {formik.touched.contentName && formik.errors.contentName && <FormFeedback>{formik.errors.contentName}</FormFeedback>}
                      </Col>
                    </Row>

                    {/* DATE */}
                    <Row>
                      <Col md={6}>
                        <Row>
                          <Col md={4} className="d-flex align-items-start">
                            <Label className="text-xl fw-semibold my-auto">วันที่เริ่ม</Label>
                          </Col>
                          <Col md={8}>
                            <div className="datepicker">
                              <DatePicker
                                useRange={false}
                                asSingle={true}
                                value={{
                                  startDate: formik.values.startDate,
                                  endDate: formik.values.startDate,
                                }}
                                onChange={handleStartDateChange}
                                displayFormat="DD/MM/YYYY"
                                inputClassName={`form-control ${formik.touched.startDate && formik.errors.startDate ? "is-invalid" : ""}`}
                              />
                              {formik.touched.startDate && formik.errors.startDate && <FormFeedback>{formik.errors.startDate}</FormFeedback>}
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      <Col md={6}>
                        <Row>
                          <Col md={4} className="d-flex align-items-start">
                            <Label className="text-xl fw-semibold my-auto">วันที่สิ้นสุด</Label>
                          </Col>
                          <Col md={8}>
                            <div className="datepicker">
                              <DatePicker
                                useRange={false}
                                asSingle={true}
                                value={{
                                  startDate: formik.values.endDate,
                                  endDate: formik.values.endDate,
                                }}
                                onChange={handleEndDateChange}
                                displayFormat="DD/MM/YYYY"
                                inputClassName={`form-control ${formik.touched.endDate && formik.errors.endDate ? "is-invalid" : ""}`}
                                disabled={!formik.values.startDate}
                                disabledDates={
                                  formik.values.startDate
                                    ? [
                                        {
                                          startDate: new Date(0),
                                          endDate: new Date(new Date(formik.values.startDate).getTime() - 24 * 60 * 60 * 1000), // One day before start date
                                        },
                                      ]
                                    : []
                                }
                              />
                            </div>
                            {formik.touched.endDate && formik.errors.endDate && <FormFeedback>{formik.errors.endDate}</FormFeedback>}
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    {/* URL */}
                    <Row>
                      <Col md={2} className="d-flex align-items-start">
                        <Label className="text-xl fw-semibold my-auto">ชื่อลิงค์ URL</Label>
                      </Col>
                      <Col md={10}>
                        <Input type="text" name="contentUrl" placeholder="กรุณากรอก URL" value={formik.values.contentUrl || ""} onChange={formik.handleChange} onBlur={formik.handleBlur} invalid={formik.touched.contentUrl && formik.errors.contentUrl} />
                        {formik.touched.contentUrl && formik.errors.contentUrl && <FormFeedback>{formik.errors.contentUrl}</FormFeedback>}
                      </Col>
                    </Row>

                    {/* BANNER */}
                    <Row>
                      <Col md={2} className="d-flex align-items-start">
                        <Label className="text-xl fw-semibold">อัปโหลดแบนเนอร์ข่าว</Label>
                      </Col>
                      <Col md={10}>
                        <div className={formik.touched.contentImg && formik.errors.contentImg ? 'is-invalid' : ''}>
                          <ImageUpload 
                            initFile={contentImage} 
                            message="อัพโหลดแบนเนอร์เนื้อหาข่าว" 
                            accept="PNG, JPG, JPEG, WEBP" 
                            maxSize={2 * 1024 * 1024} 
                            onFileChange={handleImageChange} 
                          />
                        </div>
                        {formik.touched.contentImg && formik.errors.contentImg && (
                          <div className="invalid-feedback d-block">
                            {formik.errors.contentImg}
                          </div>
                        )}
                      </Col>
                    </Row>

                    {/* STATUS */}
                    <Row>
                      <Col md={2} className="d-flex align-items-start">
                        <Label className="text-xl fw-semibold my-auto">สถานะหลังสร้าง</Label>
                      </Col>
                      <Col md={10}>
                        <div className="d-flex gap-4">
                          {statusTypes.map((type) => (
                            <div key={type.value} className="d-flex align-items-center gap-2">
                              <Input type="radio" id={`status${type.value}`} name="isHighlight" value={type.value === "highlight"} defaultChecked={formik.values.isHighlight === (type.value === "highlight")} onChange={formik.handleChange} onBlur={formik.handleBlur} invalid={formik.touched.isHighlight && Boolean(formik.errors.isHighlight)} className="mt-2" />
                              <Label check className="text-xl" htmlFor={`status${type.value}`}>
                                {type.label}
                              </Label>
                            </div>
                          ))}
                          {formik.touched.isHighlight && formik.errors.isHighlight && <FormFeedback className="d-block">{formik.errors.isHighlight}</FormFeedback>}
                        </div>
                      </Col>
                    </Row>

                    {/* CONTENTS */}
                    <Row>
                      <Col md={2} className="d-flex align-items-start">
                        <Label className="text-xl fw-semibold">เนื้อหา</Label>
                      </Col>
                      <Col md={10}>
                        <CKEditor
                          editor={ClassicEditor}
                          data={formik.values.contentDetail || ""}
                          onChange={(event, editor) => {
                            const data = editor.getData();
                            formik.setFieldValue("contentDetail", data);
                          }}
                          onBlur={() => formik.setFieldTouched("contentDetail", true)}
                        />
                        {formik.touched.contentDetail && formik.errors.contentDetail && <FormFeedback>{formik.errors.contentDetail}</FormFeedback>}
                      </Col>
                    </Row>
                  </div>
                </CardBody>
              )}
            </Card>
            <div className="text-end mt-4">
              <div className="d-flex justify-content-between gap-3 w-100">
                <Button color="light" outline className="w-52" onClick={() => navigate("/contents")} disabled={formik.isSubmitting || isAdding}>
                  ยกเลิก
                </Button>
                <Button color="primary" type="submit" className="w-52" disabled={formik.isSubmitting || isAdding || (formik.submitCount > 0 && (!formik.isValid || !formik.dirty))}>
                  {isAdding ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      กำลังบันทึก...
                    </>
                  ) : (
                    "บันทึก"
                  )}
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </Container>
    </div>
  );
};

export default withTranslation()(ContentForm);
